import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useSelector } from 'react-redux'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'

import {
  DocumentDocument,
  DocumentSignatory,
  DocumentSignatorySignedBlockContent,
  DocumentSigningTemplateBlock,
} from '../../../lib/db/db.types'
import {
  selectUserId,
  selectUserProfile,
} from '../../../store/slices/userSlice'
import {
  getDocumentDownloadUrl,
  subscribeToDocument,
  updateDocumentSignatories,
  updateDocumentSigningStatus,
  updateDocumentSigningTemplateBlocks,
  updateDocumentSigningTemplatePageDimensions,
} from '../../../lib/db/db'
import { WidthWrap } from '../../scene/WidthWrap'
import { selectWorkspaces } from '../../../store/slices/workspaceSlice'
import { onSignDocument } from '../../../lib/api'

import { PdfEditor } from './components/PdfEditor'
import { ManageSigners } from './components/ManageSigners'
import { DocumentSigned } from './components/DocumentSigned'
import { SigningInProgress } from './components/SigningInProgress'
import { SignDocument } from './components/SignDocument'

const useDocumentData = ({
  documentId,
  workspaceId,
}: {
  documentId: string
  workspaceId: string
}) => {
  const [data, setData] = useState<DocumentDocument | null>(null)

  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [signedDocumentUrl, setSignedDocumentUrl] = useState<string | null>(
    null,
  )

  const storageRef = data?.storageRef
  const signedStorageRef = data?.signing?.signedDocumentStorageRef

  useEffect(() => {
    function onUpdate(doc: DocumentDocument) {
      setData(doc)
    }
    subscribeToDocument({ workspaceId, documentId, onUpdate })
  }, [workspaceId, documentId])

  useEffect(() => {
    if (storageRef) {
      getDocumentDownloadUrl(storageRef).then((url) => {
        setDocumentUrl(url)
      })
    }
  }, [storageRef])

  useEffect(() => {
    if (signedStorageRef) {
      getDocumentDownloadUrl(signedStorageRef).then((url) => {
        setSignedDocumentUrl(url)
      })
    }
  }, [signedStorageRef])

  return {
    document: data,
    storageRef,
    documentUrl,
    signedStorageRef,
    signedDocumentUrl,
  }
}

export const SignDocumentFlow = ({
  workspaceId,
  documentId,
}: {
  workspaceId: string
  documentId: string
}) => {
  const navigate = useNavigate()
  const userId = useSelector(selectUserId)
  const userProfile = useSelector(selectUserProfile)

  const workspaces = useSelector(selectWorkspaces)
  const workspace = workspaces.find((w) => w.id === workspaceId)
  const workspaceCollaborators = useMemo(() => {
    return Object.entries(workspace?.collaborators ?? {}).map(
      ([id, collaborator]) => ({
        id,
        name: collaborator.name,
        email: collaborator.email,
      }),
    )
  }, [workspace])

  const [setupStep, setSetupStep] = useState<'signatories' | 'template'>(
    'signatories',
  )
  const [acceptedBlocks, setAcceptedBlocks] = useState<
    DocumentSignatorySignedBlockContent[]
  >([])

  const documentRef = useRef<DocumentDocument | null>(null)

  const {
    document,
    storageRef,
    documentUrl,
    signedStorageRef,
    signedDocumentUrl,
  } = useDocumentData({ documentId, workspaceId })

  const currentSignerId = document?.signing?.currentSignerId
  const status = useMemo(() => document?.signing?.status ?? 'setup', [document])
  const blocks = useMemo(
    () => document?.signing?.template?.blocks ?? [],
    [document],
  )
  const signatories = useMemo(
    () => document?.signing?.signatories ?? {},
    [document],
  )

  const currentSignerBlocks = useMemo(() => {
    if (currentSignerId) {
      return blocks.filter((block) => block.signatoryId === currentSignerId)
    }
    return []
  }, [currentSignerId, blocks])

  const allBlocksAccepted = useMemo(() => {
    return currentSignerBlocks.every((block) =>
      acceptedBlocks.some((b) => b.id === block.id),
    )
  }, [acceptedBlocks, currentSignerBlocks])

  // Add color mapping for signatories
  const signatoryColors = useMemo(() => {
    const colors = [
      'rgb(219, 234, 254)', // blue-100
      'rgb(220, 252, 231)', // green-100
      'rgb(254, 226, 226)', // red-100
      'rgb(250, 232, 255)', // purple-100
      'rgb(254, 243, 199)', // yellow-100
      'rgb(245, 208, 254)', // pink-100
    ]

    return Object.keys(signatories).reduce(
      (acc, id, index) => ({
        ...acc,
        [id]: colors[index % colors.length],
      }),
      {} as Record<string, string>,
    )
  }, [signatories])

  const handleSignatoriesChange = async (
    signatories: Record<string, DocumentSignatory>,
  ) => {
    if (!document) return
    if (!userId) return

    try {
      await updateDocumentSignatories({
        workspaceId,
        documentId,
        signatories,
      })
    } catch (error) {
      console.error(error)
      toast('Error updating signatories, please try again')
    }
  }

  const sendDocumentForSigning = async () => {
    if (!document) return
    if (!userId) return

    try {
      if (status === 'setup') {
        await updateDocumentSigningStatus({
          workspaceId,
          documentId,
          status: 'signing-in-progress',
          userId,
        })
      } else {
        toast('Unable to move to next step')
      }
    } catch (error) {
      console.error(error)
      toast('Error changing status, please try again')
    }
  }

  const handleBlocksChange = async (blocks: DocumentSigningTemplateBlock[]) => {
    await updateDocumentSigningTemplateBlocks({
      workspaceId,
      documentId,
      blocks,
    })
  }

  const handlePageDimensionsChange = async (
    pageDimensions: Array<{ width: number; height: number }>,
  ) => {
    await updateDocumentSigningTemplatePageDimensions({
      workspaceId,
      documentId,
      pageDimensions,
    })
  }

  useEffect(() => {
    if (!documentRef.current) {
      documentRef.current = document
      if (Object.keys(document?.signing?.signatories ?? {}).length > 0) {
        setSetupStep('template')
      } else {
        setSetupStep('signatories')
      }
    }
  }, [document])

  const canProgress = () => {
    switch (status) {
      case 'setup':
        if (setupStep === 'signatories') {
          return Object.keys(document?.signing?.signatories || {}).length > 0
        } else {
          // Template step - check if all signatories have at least one block
          const signatories = document?.signing?.signatories || {}
          return Object.keys(signatories).every((signatoryId) =>
            blocks.some((block) => block.signatoryId === signatoryId),
          )
        }

      case 'signing-in-progress':
        return allBlocksAccepted

      default:
        return false
    }
  }

  const handleBack = () => {
    if (status === 'setup') {
      if (setupStep === 'template') {
        setSetupStep('signatories')
      } else {
        navigate(`/workspace/${workspaceId}/documents/`)
      }
    } else if (status === 'signing-in-progress') {
      if (!userId) return
      updateDocumentSigningStatus({
        workspaceId,
        documentId,
        status: 'setup',
        userId,
      })
    } else {
      navigate(`/workspace/${workspaceId}/documents/`)
    }
  }

  const handleNext = async () => {
    if (status === 'setup') {
      if (setupStep === 'signatories') {
        setSetupStep('template')
      } else {
        await sendDocumentForSigning()
      }
    } else if (status === 'signing-in-progress') {
      const response = await onSignDocument({
        workspaceId,
        documentId,
        acceptedBlocks,
      })
      if (response.data.status === 'OK') {
        toast.success('Document signed successfully')
      } else {
        toast.error(`Error signing document: ${response.data.error}`)
      }
    }
  }

  const handleAcceptBlock = (block: DocumentSignatorySignedBlockContent) => {
    setAcceptedBlocks((prev) => [...prev, block])
  }

  const renderBottomBar = () => {
    const isSigned = status === 'signed' || status === 'signing-complete'
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-4">
        <WidthWrap>
          <div className="flex justify-between items-center">
            <button
              onClick={handleBack}
              className="px-4 py-2 rounded bg-gray-100 text-gray-600 hover:text-gray-800 disabled:opacity-50 flex items-center gap-2"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>

            {!isSigned && (
              <button
                onClick={handleNext}
                disabled={!canProgress()}
                className={`px-4 py-2 rounded text-white ${
                  canProgress()
                    ? 'bg-blue-500 hover:bg-blue-600'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {status === 'setup' &&
                  setupStep === 'signatories' &&
                  `Continue to Template`}
                {status === 'setup' &&
                  setupStep === 'template' &&
                  `Send for Signing`}
                {status === 'signing-in-progress' && `Finish`}
              </button>
            )}
          </div>
        </WidthWrap>
      </div>
    )
  }

  if (!document || !storageRef || !documentUrl) {
    return <div>Loading...</div>
  }

  const renderSigningStep = () => {
    switch (status) {
      case 'setup':
        if (setupStep === 'signatories') {
          return (
            <WidthWrap>
              <ManageSigners
                collaborators={workspaceCollaborators}
                signatories={document.signing?.signatories || {}}
                signatoryColors={signatoryColors}
                onChange={handleSignatoriesChange}
              />
            </WidthWrap>
          )
        } else {
          return (
            <PdfEditor
              storageRef={document.storageRef}
              signatories={document.signing?.signatories || {}}
              signatoryColors={signatoryColors}
              blocks={blocks}
              onBlocksChange={handleBlocksChange}
              onPageDimensionsChange={handlePageDimensionsChange}
            />
          )
        }

      case 'signing-in-progress':
        const currentSignerId = document?.signing?.currentSignerId
        if (currentSignerId && currentSignerId === userId) {
          return (
            <SignDocument
              storageRef={storageRef}
              signatureName={userProfile?.signature?.name}
              signatureImage={userProfile?.signature?.imageSrc}
              blocks={currentSignerBlocks}
              acceptedBlocks={acceptedBlocks}
              signatoryColor={signatoryColors[currentSignerId]}
              onAcceptBlock={handleAcceptBlock}
            />
          )
        }
        return (
          <SigningInProgress
            currentSignerId={currentSignerId}
            signatories={signatories || {}}
            signatoryColors={signatoryColors}
          />
        )

      case 'signing-complete':
      case 'signed':
        return (
          <WidthWrap>
            <DocumentSigned
              status={status}
              originalStorageRef={storageRef}
              originalStorageFileUrl={documentUrl}
              signedStorageRef={signedStorageRef}
              signedStorageFileUrl={signedDocumentUrl}
            />
          </WidthWrap>
        )

      default:
        return <div>Something went wrong, please contact us.</div>
    }
  }

  return (
    <div className="w-full h-full pb-20">
      {renderSigningStep()}
      {renderBottomBar()}
    </div>
  )
}
